import { Auth } from 'aws-amplify';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';

export enum FeatureFlag {
  AI_ASSISTANT = 'ai_assistant',
}

export type FeatureFlagMap = Record<FeatureFlag, boolean>;

const LAMBDA_ARN = 'arn:aws:lambda:us-east-1:340752802328:function:ControllershipHub-FeatureFlagHandler-dev:live';
const REGION = 'us-east-1';

/** Default values — all flags disabled for safety */
export const defaultFlags: FeatureFlagMap = {
  [FeatureFlag.AI_ASSISTANT]: false,
};

let cache: FeatureFlagMap | null = null;

/**
 * Fetches feature flags from the FeatureFlagHandler Lambda via AppConfig.
 * On localhost or if Lambda ARN is not configured, enables all flags.
 * Caches results for the session.
 */
export const fetchFeatureFlags = async (): Promise<FeatureFlagMap> => {
  if (cache) return cache;

  // Localhost: enable all flags for local development
  const isLocalhost =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isLocalhost) {
    console.log('[FeatureFlags] Localhost detected, enabling all flags');
    cache = Object.fromEntries(
      Object.values(FeatureFlag).map((flag) => [flag, true])
    ) as FeatureFlagMap;
    return cache;
  }

  // Runtime check - prevent webpack from optimizing this away
  const lambdaArn = String(LAMBDA_ARN);
  if (!lambdaArn || lambdaArn.includes('{{')) {
    console.log('[FeatureFlags] Lambda ARN not configured, disabling all flags');
    cache = defaultFlags;
    return cache;
  }

  try {
    console.log('[FeatureFlags] Calling Lambda...', lambdaArn);
    const credentials = await Auth.currentCredentials();
    const client = new LambdaClient({ region: String(REGION), credentials });

    const response = await client.send(
      new InvokeCommand({
        FunctionName: lambdaArn,
        Payload: new TextEncoder().encode(JSON.stringify({})),
      })
    );

    const responseBody = JSON.parse(new TextDecoder().decode(response.Payload));
    const flags: FeatureFlagMap = { ...defaultFlags };

    for (const flag of Object.values(FeatureFlag)) {
      if (responseBody.body?.[flag]?.enabled !== undefined) {
        flags[flag] = responseBody.body[flag].enabled;
      }
    }

    cache = flags;
    return cache;
  } catch (error) {
    console.error('[FeatureFlags] Error fetching flags:', error);
    cache = defaultFlags;
    return cache;
  }
};

export const isFeatureEnabled = (flags: FeatureFlagMap, flag: FeatureFlag): boolean => {
  return flags[flag] ?? false;
};
